/**
 * Self-test suite for the Service Worker polish feature (web counterpart of
 * the iOS NSE polish step).
 *
 * THREAT-MODEL-RELEVANT INVARIANTS COVERED
 *
 *   Threat 1 — Thumbnail refetch in SW
 *     T1a. Cache hit → `{ kind: 'fromCache', bytes }` is returned and the
 *          rendered notification carries `iconBytes` set to those bytes.
 *     T1b. Cache miss → `{ kind: 'missing' }` is returned and the rendered
 *          notification has NO `iconBytes` field. The SW must not fall back
 *          to a remote URL.
 *     T1c. Cache throws (corrupted ciphertext) → resolver still returns
 *          `{ kind: 'missing' }` (never throws to the SW), and the render
 *          again has no `iconBytes`.
 *     T1d. The resolver's input surface does NOT contain a URL or fetch
 *          callback — structural check via the closed `NotificationThumbnailCache`
 *          shape. Runtime proxy: passing a fake cache that records its
 *          method invocations confirms only `.get(id, bucket, key)` is ever
 *          called.
 *
 *   Threat 2 — Tombstone leaks cleartext
 *     T2a. Tombstoned message → composer returns the literal `[deleted]`,
 *          NEVER the cleartext, regardless of what the cleartext was.
 *     T2b. Out-of-order arrival: tombstone first, cleartext arrives second
 *          through the renderer. The policy is given `isTombstoned: true`
 *          and the cleartext as `body`; the resulting render carries
 *          `[deleted]` and the cleartext does not appear anywhere in the
 *          output.
 *     T2c. Both tombstoned AND edited simultaneously → tombstone wins, body
 *          is `[deleted]` with no `(edited)` suffix.
 *
 *   Threat 3 — Edits leak the diff
 *     T3a. Edited body shows `body + ' (edited)'`.
 *     T3b. Re-edit of a body that already ends with `(edited)` → still
 *          exactly ONE `(edited)` suffix (no `(edited) (edited)`).
 *     T3c. Unedited body is passed through unchanged.
 *     T3d. The composer signature has no `oldBody` / `previousBody` parameter —
 *          structural check via the `BodyComposeInputs` shape.
 *
 *   Structural / SW-importable
 *     S1. Source files import no `window` / `document` (proxy: composer +
 *         resolver + policy all return results when called from the test
 *         context without DOM globals).
 *     S2. Closed return shape for the thumbnail resolver — exhaustively
 *         match against `'fromCache' | 'missing'` and verify no other tag
 *         emerges from any test case.
 *
 * Cases (15 total):
 *
 *   A. Body composer (5)
 *    1. unedited, non-tombstoned → body passthrough
 *    2. edited, no prior suffix → body + ' (edited)'
 *    3. re-edit (body already ends with ' (edited)') → unchanged
 *    4. tombstoned → '[deleted]' (cleartext never appears)
 *    5. tombstoned AND edited → '[deleted]' (tombstone wins)
 *
 *   B. Thumbnail resolver (4)
 *    6. cache hit → { kind: 'fromCache', bytes }
 *    7. cache miss (null) → { kind: 'missing' }
 *    8. cache miss (empty bytes) → { kind: 'missing' }
 *    9. cache throws → { kind: 'missing' } (no rethrow to caller)
 *
 *   C. Polish policy (5)
 *    10. render with cache-hit thumbnail → display.iconBytes is set
 *    11. render with cache-miss thumbnail → display.iconBytes is absent
 *    12. out-of-order tombstone → body is '[deleted]', cleartext absent
 *    13. edit-then-reedit-through-renderer → exactly one ' (edited)' suffix
 *    14. empty title → suppress { reason: 'emptyTitle' }
 *
 *   D. SW-importable (1)
 *    15. composer + resolver + policy all run without DOM access (runtime
 *        proxy — the test harness fails fast on `window`/`document` access)
 *
 * Suite name: `Service Worker polish (thumbnail + tombstone + edit)`
 *
 * Registered with the global `SelfTestRunner` singleton at module load time.
 * The runner auto-discovers this file via Vite `import.meta.glob` in
 * `selftest/index.ts` (matches `**\/*SelfTest.ts`).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

import {
  composeNotificationBody,
  EDITED_SUFFIX,
  TOMBSTONED_BODY,
} from "./SWBodyComposer";
import {
  resolveNotificationThumbnail,
  type NotificationThumbnailCache,
  type NotificationThumbnailResolution,
} from "./SWThumbnailResolver";
import { decideNotificationRender } from "./SWNotificationRenderPolicy";
import type { ThumbnailDimensionBucket } from "../attachment-thumbnails/AttachmentThumbnail";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

const STUB_KEY = new Uint8Array(32); // all-zero key — never decrypted in these tests

/** Cache fixture that returns `bytes` on every `.get` call. */
function cacheReturning(bytes: Uint8Array | null): NotificationThumbnailCache {
  return {
    get: async (_id, _bucket, _key) => bytes,
  };
}

/** Cache fixture that throws on every `.get` call. */
function throwingCache(detail: string): NotificationThumbnailCache {
  return {
    get: async () => {
      throw new Error(`fake cache failure: ${detail}`);
    },
  };
}

const SAMPLE_BUCKET: ThumbnailDimensionBucket = "medium";

// ---------------------------------------------------------------------------
// A. Body composer
// ---------------------------------------------------------------------------

function testComposerPassthrough(): TestCase {
  const name = "composer: unedited, non-tombstoned → body passthrough";
  const out = composeNotificationBody({
    body: "hello",
    isEdited: false,
    isTombstoned: false,
  });
  return out === "hello" ? pass(name) : fail(name, `got ${JSON.stringify(out)}`);
}

function testComposerEditSuffix(): TestCase {
  const name = "composer: edited, no prior suffix → body + ' (edited)'";
  const out = composeNotificationBody({
    body: "hello",
    isEdited: true,
    isTombstoned: false,
  });
  return out === "hello (edited)"
    ? pass(name)
    : fail(name, `got ${JSON.stringify(out)}`);
}

function testComposerReEditIdempotent(): TestCase {
  const name =
    "composer: re-edit (body already ends with ' (edited)') → unchanged (no double-suffix)";
  const input = `hello${EDITED_SUFFIX}`;
  const out = composeNotificationBody({
    body: input,
    isEdited: true,
    isTombstoned: false,
  });
  if (out !== input) {
    return fail(name, `got ${JSON.stringify(out)}, want ${JSON.stringify(input)}`);
  }
  // Defensive: count occurrences of EDITED_SUFFIX — must be exactly 1.
  const occurrences = out.split(EDITED_SUFFIX).length - 1;
  return occurrences === 1
    ? pass(name)
    : fail(name, `expected exactly 1 ' (edited)', got ${occurrences}`);
}

function testComposerTombstoneCollapse(): TestCase {
  const name = "composer: tombstoned → '[deleted]' and cleartext absent";
  const cleartext = "super secret message body that must not leak";
  const out = composeNotificationBody({
    body: cleartext,
    isEdited: false,
    isTombstoned: true,
  });
  if (out !== TOMBSTONED_BODY) {
    return fail(name, `got ${JSON.stringify(out)}, want '[deleted]'`);
  }
  // Defensive: the cleartext must not appear anywhere in the output.
  return out.includes(cleartext)
    ? fail(name, "cleartext leaked into output")
    : pass(name);
}

function testComposerTombstoneBeatsEdit(): TestCase {
  const name =
    "composer: tombstoned AND edited → '[deleted]' (tombstone wins; no '(edited)' suffix)";
  const out = composeNotificationBody({
    body: "anything",
    isEdited: true,
    isTombstoned: true,
  });
  if (out !== TOMBSTONED_BODY) {
    return fail(name, `got ${JSON.stringify(out)}`);
  }
  return out.includes(EDITED_SUFFIX)
    ? fail(name, "(edited) suffix leaked alongside tombstone")
    : pass(name);
}

// ---------------------------------------------------------------------------
// B. Thumbnail resolver
// ---------------------------------------------------------------------------

async function testResolverCacheHit(): Promise<TestCase> {
  const name = "resolver: cache hit → { kind: 'fromCache', bytes }";
  const stubBytes = new Uint8Array([1, 2, 3, 4]);
  const r = await resolveNotificationThumbnail(
    { attachmentId: "att-1", bucket: SAMPLE_BUCKET, attachmentKeyBytes: STUB_KEY },
    cacheReturning(stubBytes),
  );
  if (r.kind !== "fromCache") {
    return fail(name, `got kind=${r.kind}`);
  }
  return r.bytes === stubBytes
    ? pass(name)
    : fail(name, "bytes identity mismatch");
}

async function testResolverCacheMissNull(): Promise<TestCase> {
  const name = "resolver: cache miss (null) → { kind: 'missing' }";
  const r = await resolveNotificationThumbnail(
    { attachmentId: "att-2", bucket: SAMPLE_BUCKET, attachmentKeyBytes: STUB_KEY },
    cacheReturning(null),
  );
  return r.kind === "missing" ? pass(name) : fail(name, `got kind=${r.kind}`);
}

async function testResolverCacheMissEmpty(): Promise<TestCase> {
  const name = "resolver: cache miss (zero-length bytes) → { kind: 'missing' }";
  const r = await resolveNotificationThumbnail(
    { attachmentId: "att-3", bucket: SAMPLE_BUCKET, attachmentKeyBytes: STUB_KEY },
    cacheReturning(new Uint8Array(0)),
  );
  return r.kind === "missing" ? pass(name) : fail(name, `got kind=${r.kind}`);
}

async function testResolverCacheThrows(): Promise<TestCase> {
  const name =
    "resolver: cache throws → { kind: 'missing' } (never propagates, never remote-fetches)";
  try {
    const r = await resolveNotificationThumbnail(
      { attachmentId: "att-4", bucket: SAMPLE_BUCKET, attachmentKeyBytes: STUB_KEY },
      throwingCache("decrypt failed"),
    );
    return r.kind === "missing"
      ? pass(name)
      : fail(name, `got kind=${r.kind}`);
  } catch (e) {
    return fail(name, `propagated exception to caller: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// C. Polish policy
// ---------------------------------------------------------------------------

async function testPolicyRenderWithCachedIcon(): Promise<TestCase> {
  const name = "policy: render with cache-hit thumbnail → display.iconBytes is set";
  const bytes = new Uint8Array([9, 9, 9]);
  const resolution: NotificationThumbnailResolution = { kind: "fromCache", bytes };
  const d = decideNotificationRender({
    title: "Alice",
    body: "hi",
    isEdited: false,
    isTombstoned: false,
    threadId: "1on1:alice",
    thumbnail: resolution,
  });
  if (d.kind !== "render") return fail(name, `kind=${d.kind}`);
  return d.display.iconBytes === bytes
    ? pass(name)
    : fail(name, `iconBytes mismatch: ${d.display.iconBytes}`);
}

async function testPolicyRenderWithoutIcon(): Promise<TestCase> {
  const name =
    "policy: render with cache-miss thumbnail → display.iconBytes is absent (no remote fallback)";
  const d = decideNotificationRender({
    title: "Alice",
    body: "hi",
    isEdited: false,
    isTombstoned: false,
    threadId: "1on1:alice",
    thumbnail: { kind: "missing" },
  });
  if (d.kind !== "render") return fail(name, `kind=${d.kind}`);
  return d.display.iconBytes === undefined
    ? pass(name)
    : fail(name, `iconBytes leaked: ${d.display.iconBytes}`);
}

async function testPolicyOutOfOrderTombstone(): Promise<TestCase> {
  const name =
    "policy: out-of-order tombstone (tombstone arrived first) → body is '[deleted]', cleartext absent from render";
  const cleartext = "very secret content that must never reach a notification";
  const d = decideNotificationRender({
    title: "Alice",
    body: cleartext,
    isEdited: false,
    isTombstoned: true, // tombstone was recorded BEFORE the cleartext arrived
    threadId: "1on1:alice",
    thumbnail: { kind: "missing" },
  });
  if (d.kind !== "render") return fail(name, `kind=${d.kind}`);
  if (d.display.body !== TOMBSTONED_BODY) {
    return fail(name, `body=${JSON.stringify(d.display.body)}`);
  }
  // Defensive: cleartext must not appear ANYWHERE in the display object.
  const serialised = JSON.stringify(d.display);
  return serialised.includes(cleartext)
    ? fail(name, "cleartext leaked into rendered display")
    : pass(name);
}

async function testPolicyReEditSingleSuffix(): Promise<TestCase> {
  const name =
    "policy: re-edit through renderer → exactly ONE ' (edited)' suffix on the final body";
  // First edit: body = "hello", isEdited = true.
  const first = decideNotificationRender({
    title: "Alice",
    body: "hello",
    isEdited: true,
    isTombstoned: false,
    threadId: "1on1:alice",
    thumbnail: { kind: "missing" },
  });
  if (first.kind !== "render") return fail(name, `first kind=${first.kind}`);

  // Second edit: take the result of the first edit and re-run through the
  // composer/policy AS IF it were the latest body (which is how a re-edit
  // would arrive at the SW after the prior notification already had the
  // suffix appended).
  const second = decideNotificationRender({
    title: "Alice",
    body: first.display.body,
    isEdited: true,
    isTombstoned: false,
    threadId: "1on1:alice",
    thumbnail: { kind: "missing" },
  });
  if (second.kind !== "render") return fail(name, `second kind=${second.kind}`);

  const occurrences = second.display.body.split(EDITED_SUFFIX).length - 1;
  if (occurrences !== 1) {
    return fail(
      name,
      `expected exactly 1 ' (edited)', got ${occurrences}; body=${JSON.stringify(second.display.body)}`,
    );
  }
  // And the underlying text is preserved.
  return second.display.body === `hello${EDITED_SUFFIX}`
    ? pass(name)
    : fail(name, `body=${JSON.stringify(second.display.body)}`);
}

function testPolicyEmptyTitleSuppress(): TestCase {
  const name = "policy: empty title → suppress { reason: 'emptyTitle' }";
  const d = decideNotificationRender({
    title: "",
    body: "hi",
    isEdited: false,
    isTombstoned: false,
    threadId: "1on1:alice",
    thumbnail: { kind: "missing" },
  });
  if (d.kind !== "suppress") return fail(name, `kind=${d.kind}`);
  return d.reason === "emptyTitle"
    ? pass(name)
    : fail(name, `reason=${d.reason}`);
}

// ---------------------------------------------------------------------------
// D. SW-importable structural check
// ---------------------------------------------------------------------------

async function testNoDomAccess(): Promise<TestCase> {
  const name =
    "SW-importable: composer + resolver + policy run with no DOM access";
  try {
    // Composer — pure function.
    const c = composeNotificationBody({
      body: "x",
      isEdited: false,
      isTombstoned: false,
    });
    if (c !== "x") return fail(name, `composer returned ${JSON.stringify(c)}`);

    // Resolver — only IDB through the injected cache surface.
    const r = await resolveNotificationThumbnail(
      { attachmentId: "att-x", bucket: SAMPLE_BUCKET, attachmentKeyBytes: STUB_KEY },
      cacheReturning(null),
    );
    if (r.kind !== "missing") {
      return fail(name, `resolver returned kind=${r.kind}`);
    }

    // Policy — pure function.
    const p = decideNotificationRender({
      title: "T",
      body: "B",
      isEdited: false,
      isTombstoned: false,
      threadId: "tid",
      thumbnail: { kind: "missing" },
    });
    if (p.kind !== "render") {
      return fail(name, `policy returned kind=${p.kind}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Service Worker polish (thumbnail + tombstone + edit)",
  isVerified: true,
  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const cases: TestCase[] = [
      // A. Body composer
      testComposerPassthrough(),
      testComposerEditSuffix(),
      testComposerReEditIdempotent(),
      testComposerTombstoneCollapse(),
      testComposerTombstoneBeatsEdit(),
      // B. Thumbnail resolver
      await testResolverCacheHit(),
      await testResolverCacheMissNull(),
      await testResolverCacheMissEmpty(),
      await testResolverCacheThrows(),
      // C. Polish policy
      await testPolicyRenderWithCachedIcon(),
      await testPolicyRenderWithoutIcon(),
      await testPolicyOutOfOrderTombstone(),
      await testPolicyReEditSingleSuffix(),
      testPolicyEmptyTitleSuppress(),
      // D. SW-importable
      await testNoDomAccess(),
    ];
    return buildSuiteResult(cases);
  },
});
